// ST-32 -Claude- Company team and role management for the Nearrme business portal.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { isNearrmeAdminRole, listCompanies, listRoles, listUsers, updateUserRole } from '../api';
import { useAdminAuth } from '../useAdminAuth';

export function AdminUsersPage() {
  const { user } = useAdminAuth();
  const [companyCode, setCompanyCode] = useState(user?.companyCode ?? '');
  const [roleEdits, setRoleEdits] = useState<Record<number, string>>({});
  const queryClient = useQueryClient();
  const isNearrmeAdmin = isNearrmeAdminRole(user?.role);

  const companies = useQuery({ queryKey: ['admin', 'companies'], queryFn: listCompanies });
  const roles = useQuery({ queryKey: ['admin', 'roles'], queryFn: listRoles });
  const users = useQuery({
    queryKey: ['admin', 'users', companyCode],
    queryFn: () => listUsers(companyCode),
    enabled: !!companyCode,
  });
  const roleMutation = useMutation({
    mutationFn: ({ userProfileId, role }: { userProfileId: number; role: string }) =>
      updateUserRole(userProfileId, role, companyCode),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users', companyCode] }),
  });

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Company team</h1>
      <p className="mt-1 text-slate-600 dark:text-slate-400">
        Manage company and store roles. Consumers are managed separately because they belong to Nearrme, not to a company.
      </p>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Card padded>
          <h2 className="text-base font-semibold text-slate-950 dark:text-slate-50">Company role management</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {isNearrmeAdmin
              ? 'Nearrme Admin can assign company roles across businesses.'
              : 'Your account can review assigned company roles. Role changes require Nearrme Admin access.'}
          </p>
          {roles.isLoading ? (
            <div className="mt-4 space-y-2">
              {[1, 2, 3].map((i) => <LoadingSkeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {(roles.data ?? []).filter((role) => !role.adminOnly && !role.consumerRole).map((role) => (
                <div key={role.roleCode} className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-slate-900 dark:text-slate-100">{role.displayName}</div>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      {role.consumerRole ? 'Consumer' : role.adminOnly ? 'Admin' : 'Business'}
                    </span>
                  </div>
                  <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-400">{role.description}</p>
                  <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-400">{role.profileSummary}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {role.permissions.slice(0, 3).map((permission) => (
                      <span key={permission} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {permission}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card padded>
          <h2 className="text-base font-semibold text-slate-950 dark:text-slate-50">Access model</h2>
          <div className="mt-3 space-y-3 text-sm text-slate-600 dark:text-slate-400">
            <p><strong className="text-slate-900 dark:text-slate-100">Company:</strong> Owner, Admin, Manager.</p>
            <p><strong className="text-slate-900 dark:text-slate-100">Store:</strong> Store Owner and Store Manager.</p>
            <p><strong className="text-slate-900 dark:text-slate-100">Consumer:</strong> separate Nearrme-owned customer account.</p>
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <select
          value={companyCode}
          onChange={(e) => setCompanyCode(e.target.value)}
          className="h-9 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="">Select company</option>
          {(companies.data ?? []).map((c) => (
            <option key={c.companyCode ?? ''} value={c.companyCode ?? ''}>
              {c.companyCode} - {c.companyName}
            </option>
          ))}
        </select>
      </div>

      {users.isLoading && companyCode && (
        <div className="mt-6 space-y-2">
          {[1, 2, 3].map((i) => <LoadingSkeleton key={i} className="h-12 w-full" />)}
        </div>
      )}

      {users.error && (
        <Card padded className="mt-6">
          <div className="text-red-600 dark:text-red-400 text-sm">{(users.error as Error).message}</div>
        </Card>
      )}

      {users.data && (
        <>
          <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            {users.data.length} {users.data.length === 1 ? 'user' : 'users'}
          </div>
          {users.data.length === 0 ? (
            <EmptyState title="No users" />
          ) : (
            <Card padded={false} className="mt-2 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Name</th>
                    <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Email</th>
                    <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Phone</th>
                    <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Role</th>
                    <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Status</th>
                    <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {users.data.map((u) => {
                    const userProfileId = u.userProfileId ?? 0;
                    const selectedRole = roleEdits[userProfileId] ?? u.role ?? '';
                    return (
                      <tr key={u.userProfileId ?? u.email} className="hover:bg-slate-50 dark:hover:bg-slate-900">
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                          {[u.firstName, u.lastName].filter(Boolean).join(' ') || '-'}
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{u.email ?? '-'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{u.phoneNumber ?? '-'}</td>
                        <td className="px-4 py-3">
                          {isNearrmeAdmin ? (
                            <select
                              value={selectedRole}
                              onChange={(e) => setRoleEdits((current) => ({ ...current, [userProfileId]: e.target.value }))}
                              className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                            >
                              {(roles.data ?? []).filter((role) => !role.adminOnly && !role.consumerRole).map((role) => (
                                <option key={role.roleCode} value={role.roleCode}>{role.displayName}</option>
                              ))}
                            </select>
                          ) : (
                            u.role && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400">
                                {u.role}
                              </span>
                            )
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {u.isActive ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400">Active</span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">Inactive</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isNearrmeAdmin ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={!userProfileId || selectedRole === u.role || roleMutation.isPending}
                              onClick={() => roleMutation.mutate({ userProfileId, role: selectedRole })}
                            >
                              Save role
                            </Button>
                          ) : (
                            <span className="text-xs text-slate-500 dark:text-slate-400">Read only</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {roleMutation.error && (
                <div className="border-t border-slate-200 px-4 py-3 text-sm text-red-600 dark:border-slate-800 dark:text-red-400">
                  {(roleMutation.error as Error).message}
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
